/**
 * ModalInnovation Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A specialized modal component for displaying innovation-related content with:
 * - Innovation-specific styling and layout
 * - Fullscreen capability with toggle functionality
 * - Driver content display with proper formatting
 * - Keyboard navigation (Escape key support)
 * - Small modal variant for compact displays
 * - HTML content parsing for rich text display
 * - Custom innovation theme styling
 * 
 * @param {Object} data - Innovation data containing title and description
 * @param {boolean} show - Whether the modal is visible
 * @param {function} onClose - Callback function when modal is closed
 * @param {boolean} allowFullscreen - Whether fullscreen mode is available
 * @param {boolean} small - Whether to display in small modal mode
 * @param {boolean} showImage - Whether to display images (currently unused)
 * @returns {JSX.Element|null} Modal component or null if not shown
 */
import React, { useEffect, useState } from 'react';
import Parser from 'html-react-parser';
import './Modal.css';
import './ModalInnovation.css';

/**
 * ModalInnovation component specialized for displaying innovation content
 * with custom styling and fullscreen support
 */
function ModalInnovation({ data, show, onClose, allowFullscreen = false, small = false, showImage = true }) {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    if (!show) return null;

    return (
        <div className={`innovation-modal custom-modal ${isFullScreen ? 'fullscreen' : ''} ${small ? 'small-modal' : ''}`}>
            <div className="modal-content">
                <div className="modal-header">
                    <span id="modalTitle">{data.title}</span>
                    <div>
                        {allowFullscreen && (
                            <span
                                className="modalfullscreen fullscreen-btn fas fa-expand"
                                onClick={toggleFullScreen}
                            ></span>
                        )}
                        <span
                            className="modalclose pointer fas fa-times"
                            onClick={onClose}
                        ></span>
                    </div>
                </div>
                <div className="modal-body">
                    <>
                        <div className={'content-container'}>
                            <div className="driver-content">
                                {data.description && <p>{Parser(data.description)}</p>}
                            </div>
                        </div>
                    </>
                </div>
            </div>
        </div>
    );
}

export default ModalInnovation;
